<!-- src/shared/components/ui/NotoryStarfield.vue -->
<!--
  Notory "Copper Constellation" Starfield - dynamischer, cursor-reaktiver
  Hintergrund (Kupfer/Patina auf transparent, edge-masked). Portiert von
  notory.io (js/main.js), damit ots.notory.io identisch aussieht.
  Respektiert prefers-reduced-motion + Touch (dann nur ein statisches Bild).
-->
<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref } from 'vue';

  const canvas = ref<HTMLCanvasElement | null>(null);
  let raf: number | null = null;
  let cleanup: (() => void) | null = null;

  onMounted(() => {
    const sf = canvas.value;
    if (!sf) return;
    const ctx = sf.getContext('2d');
    if (!ctx) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const touch = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let nodes: Array<{ x: number; y: number; vx: number; vy: number; r: number }> = [];
    const mouse = { x: -9999, y: -9999 };

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      sf.width = w * dpr;
      sf.height = h * dpr;
      sf.style.width = w + 'px';
      sf.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const build = () => {
      const count = Math.min(Math.round((w * h) / 13000), 92);
      nodes = [];
      for (let i = 0; i < count; i++) {
        nodes.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.16,
          vy: (Math.random() - 0.5) * 0.16,
          r: Math.random() * 1.5 + 0.95,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 140) {
            ctx.strokeStyle = 'rgba(210,130,80,' + (1 - d / 140) * 0.44 + ')';
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      ctx.shadowColor = 'rgba(227,160,106,0.9)';
      for (let k = 0; k < nodes.length; k++) {
        const n = nodes[k];
        const mdx = n.x - mouse.x;
        const mdy = n.y - mouse.y;
        const md = Math.sqrt(mdx * mdx + mdy * mdy);
        const bright = md < 160 ? 1 - md / 160 : 0;
        ctx.beginPath();
        ctx.shadowBlur = 3 + bright * 7;
        ctx.fillStyle =
          bright > 0.15
            ? 'rgba(240,238,233,' + (0.55 + bright * 0.45) + ')'
            : 'rgba(230,165,110,' + (0.55 + bright * 0.45) + ')';
        ctx.arc(n.x, n.y, n.r + bright * 1.9, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;
    };

    const step = () => {
      for (const n of nodes) {
        const mdx = mouse.x - n.x;
        const mdy = mouse.y - n.y;
        const md = Math.sqrt(mdx * mdx + mdy * mdy);
        if (md < 150 && md > 0.5) {
          const p = (1 - md / 150) * 0.35;
          n.x += (mdx / md) * p;
          n.y += (mdy / md) * p;
        }
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
        n.x = Math.max(0, Math.min(w, n.x));
        n.y = Math.max(0, Math.min(h, n.y));
      }
      draw();
      raf = requestAnimationFrame(step);
    };

    resize();
    build();

    if (reduce || touch) {
      draw();
      return;
    }

    const onResize = () => {
      resize();
      build();
    };
    const onMove = (e: PointerEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    const onVis = () => {
      if (document.hidden) {
        if (raf) {
          cancelAnimationFrame(raf);
          raf = null;
        }
      } else if (!raf) {
        raf = requestAnimationFrame(step);
      }
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerleave', onLeave);
    document.addEventListener('visibilitychange', onVis);
    raf = requestAnimationFrame(step);

    cleanup = () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
      document.removeEventListener('visibilitychange', onVis);
    };
  });

  onBeforeUnmount(() => {
    if (raf) cancelAnimationFrame(raf);
    if (cleanup) cleanup();
  });
</script>

<template>
  <canvas
    ref="canvas"
    aria-hidden="true"
    class="nv-starfield"></canvas>
</template>

<style scoped>
  .nv-starfield {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
    -webkit-mask-image: radial-gradient(
      135% 95% at 50% 32%,
      transparent 0%,
      transparent 12%,
      rgba(0, 0, 0, 0.82) 46%,
      #000 80%
    );
    mask-image: radial-gradient(
      135% 95% at 50% 32%,
      transparent 0%,
      transparent 12%,
      rgba(0, 0, 0, 0.82) 46%,
      #000 80%
    );
  }

  /* Nur im Dark-Theme (Notory-Look) sichtbar; im Light-Theme ausblenden. */
  :global(html:not(.dark)) .nv-starfield {
    display: none;
  }
</style>
